import { DEFAULT_VISA_STATUS } from './visaApplicationStatus';
import { isFormFilled, parseApplicationData } from './visaApplicationData';

export const DEFAULT_VISA_FILTERS = {
  search: '',
  status: '',
  fromCountry: '',
  toCountry: '',
  visaType: '',
  company: '',
  manager: '',
  dateFrom: '',
  dateTo: '',
};

export const ERROR_POOL_FILTERS = {
  ...DEFAULT_VISA_FILTERS,
  status: 'error',
};

export const FINALIZE_POOL_FILTERS = {
  ...DEFAULT_VISA_FILTERS,
  status: 'finalize',
};

export const getApplicantName = (data) => {
  const parsed = parseApplicationData(data);
  if (!parsed || typeof parsed !== 'object') return '-';
  if (!isFormFilled(parsed)) return 'Form pending';

  if (parsed.full_name) return parsed.full_name;
  if (parsed.name) return parsed.name;
  if (parsed.applicant_name) return parsed.applicant_name;

  const firstName = parsed.first_name || parsed.firstname || parsed.given_name || '';
  const lastName = parsed.last_name || parsed.lastname || parsed.surname || parsed.family_name || '';
  const combined = `${firstName} ${lastName}`.trim();
  if (combined) return combined;

  return '-';
};

const uniqueCountryOptions = (applications, field, nameField) => {
  const seen = new Set();
  const options = [];

  applications.forEach((app) => {
    const id = String(app[field] || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    options.push({
      id,
      name: app[nameField] || id,
    });
  });

  return options.sort((a, b) => a.name.localeCompare(b.name));
};

const uniqueSorted = (values) =>
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

export const getVisaFilterOptions = (applications) => ({
  fromCountries: uniqueCountryOptions(applications, 'from_country', 'from_country_name'),
  toCountries: uniqueCountryOptions(applications, 'to_country', 'to_country_name'),
  visaTypes: uniqueSorted(applications.map((app) => app.visa_type)),
  companies: uniqueSorted(applications.map((app) => app.company_name)),
});

export const hasActiveVisaFilters = (filters, { ignoreStatus = false } = {}) => {
  if (!filters || typeof filters !== 'object') return false;
  return Object.entries(filters).some(([key, value]) => {
    if (ignoreStatus && key === 'status') return false;
    return value !== '' && value != null;
  });
};

export const filterVisaApplications = (applications, filters = DEFAULT_VISA_FILTERS) =>
  applications.filter((app) => {
    if (filters.search) {
      const query = filters.search.toLowerCase().trim();
      const applicantName = getApplicantName(app.customer_visa_application_data).toLowerCase();
      const passengerId = String(app.passenger_id || '').toLowerCase();
      if (!applicantName.includes(query) && !passengerId.includes(query)) {
        return false;
      }
    }

    if (filters.status && (app.application_status || DEFAULT_VISA_STATUS) !== filters.status) {
      return false;
    }

    if (filters.fromCountry && String(app.from_country) !== String(filters.fromCountry)) {
      return false;
    }

    if (filters.toCountry && String(app.to_country) !== String(filters.toCountry)) {
      return false;
    }

    if (filters.visaType && app.visa_type !== filters.visaType) {
      return false;
    }

    if (filters.company && app.company_name !== filters.company) {
      return false;
    }

    if (filters.manager) {
      const managerId = Number(filters.manager);
      if (!app.matched_manager_ids?.includes(managerId)) {
        return false;
      }
    }

    if (filters.dateFrom) {
      const appDate = new Date(app.created_at);
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (appDate < fromDate) return false;
    }

    if (filters.dateTo) {
      const appDate = new Date(app.created_at);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (appDate > toDate) return false;
    }

    return true;
  });
